import React from 'react'
import PropTypes from 'prop-types'
import { i18n, appLocales } from '@fs/zion-locale'

import Title from './Title'
import Head from './Head'

function isZhLocale(langUrlCode) {
  return (
    (langUrlCode === 'zh-Hant' && i18n.language === 'zh') || (langUrlCode === 'zh-Hans' && i18n.language === 'zh-hans')
  )
}

/**
 *
 * Generates a canonical URL based on the provided link configuration.
 * @param {Object} linkConfig - Configuration object for link parameters.
 * @param {Array} [linkConfig.includeParams] - Array of query parameter names to include in the canonical URL. If undefined, all parameters are included. If empty, no parameters are included. If array with values, only the specified parameters are included.
 * @param {Array} [linkConfig.excludeParams] - Array of query parameter names to exclude from the canonical URL.
 * @param {string} [originalUrl=''] - The original URL to process. Defaults to an empty string.
 * @returns {string} - The generated canonical URL.
 *
 */
export function getCanonicalUrl(linkConfig, originalUrl = '') {
  if (!originalUrl) return ''
  const url = new URL(originalUrl)
  const params = url.searchParams
  const { includeParams, excludeParams } = linkConfig
  const entriesToDelete = []

  Array.from(params.entries()).forEach(([key, value]) => {
    if (
      (Array.isArray(includeParams) && (includeParams.length === 0 || !includeParams.includes(key))) ||
      (Array.isArray(excludeParams) && excludeParams.includes(key))
    ) {
      entriesToDelete.push([key, value])
    }
  })

  entriesToDelete.forEach(([key, value]) => {
    params.delete(key, value)
  })

  return url.toString()
}

/**
 * Convenience wrapper around zion-head to provide consistent meta tags for SEO and social media.
 */
export default function PageHead({
  title,
  description,
  socialShareTitle,
  socialShareDescription,
  url,
  image,
  siteName = 'FamilySearch',
  type = 'website',
  twitterSite = '@familysearch',
  twitterCreator = '@familysearch',
  twitterCard = 'summary',
  includeHreflang = true,
  hreflangConfig,
  linkConfig = { includeParams: undefined, excludeParams: [] },
}) {
  const originalUrl = url || window?.location?.href || ''
  const canonicalUrl = originalUrl ? getCanonicalUrl(linkConfig, originalUrl) : ''
  const ogDescription = socialShareDescription || description
  const ogTitle = `${socialShareTitle || title}`

  let hreflangs = []
  if (canonicalUrl) {
    // Creating array for alternate link tags in head.
    const parsedURL = new URL(canonicalUrl)
    // Checking for locale prefix in url (e.g. /zh-Hans/* /ceb/* /de/* /en/*). it will match any 2-3 chars followed by an optional hyphen
    const matches = parsedURL.pathname.match(/^\/(?<localeCode>[a-z]{2,3}(?:-[a-zA-Z0-9-]*)?)(?<restOfURL>\/?.*|$)/) // Forked from Snow with modifications to make lang code 1 capturing group. see: https://github.com/fs-webdev/snow/blob/1a99388fbb474961a2e8b65c47bc9975db70ccaf/lib/utils/getLangFromUrl.js#L2
    const languageCode = matches?.groups?.localeCode
    const restOfUrl = matches?.groups?.restOfURL

    // Need to make sure that the regex is matching a language code and not just a regular path before proceeding.
    if (includeHreflang && (languageCode === i18n.language || isZhLocale(languageCode))) {
      const languages = (hreflangConfig?.languages || appLocales)
        .filter((l) => l !== 'eo')
        // Replace zh with zh-Hant and zh-hans with zh-Hans
        .map((lang) => {
          if (lang === 'zh') return 'zh-Hant'
          if (lang === 'zh-hans') return 'zh-Hans'
          return lang
        })
      const baseUrl = parsedURL.origin
      const queryString = parsedURL.search
      hreflangs = languages.map((language) => ({
        href: `${baseUrl}/${language}${hreflangConfig?.languageUrlMap?.[language] ?? restOfUrl}${queryString}`,
        hreflang: language,
      }))
      // default hard coded to english for now
      hreflangs.push({ href: `${baseUrl}/en${restOfUrl}${queryString}`, hreflang: 'x-default' })
    }
  }

  return (
    <>
      <Title>{title}</Title>
      <Head>
        {description && <meta name="description" content={description} />}
        <link rel="canonical" href={canonicalUrl} />
        {/* Facebook */}
        <meta property="fb:app_id" content="1261365907621824" />
        <meta property="og:locale" content={i18n.language} />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:type" content={type} />
        <meta property="og:url" content={originalUrl} />
        {ogTitle && <meta property="og:title" content={ogTitle} />}
        {ogDescription && <meta property="og:description" content={ogDescription} />}
        {image?.url && <meta property="og:image" content={image.url} />}
        {image?.url && <meta property="og:image:secure_url" content={image.url} />}
        {image?.alt && <meta property="og:image:alt" content={image.alt} />}
        {!!image?.width && <meta property="og:image:width" content={`${image.width}`} />}
        {!!image?.height && <meta property="og:image:height" content={`${image.height}`} />}
        {/* Twitter */}
        {ogTitle && <meta name="twitter:title" content={ogTitle} />}
        {ogDescription && <meta name="twitter:description" content={ogDescription} />}
        <meta name="twitter:card" content={twitterCard} />
        {image?.url && <meta name="twitter:image" content={image.url} />}
        {image?.alt && <meta name="twitter:image:alt" content={image.alt} />}
        <meta name="twitter:site" content={twitterSite} />
        <meta name="twitter:creator" content={twitterCreator} />
        {hreflangs.length &&
          hreflangs.map(({ href, hreflang }) => (
            <link key={hreflang} rel="alternate" href={href} hrefLang={hreflang} />
          ))}
      </Head>
    </>
  )
}

PageHead.propTypes = {
  /** Page title.  Will show up in the browser's page tab and history list; also in the Google search results snippet.
   *  Also used in social media titles if socialShareTitle is not provided. */
  title: PropTypes.string.isRequired,

  /** Page meta description.  Will show up in Google search results snippet. Also used in social media snippets if socialShareDescription is not provided. */
  description: PropTypes.string.isRequired,

  /** Image used in social sharing previews. Aspect ratio should be 1.91:1. A good size is 1200x630. (https://socialsharepreview.com/) */
  image: PropTypes.shape({
    url: PropTypes.string,
    alt: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
  }),

  /** Social media sharing preview title (og:title). */
  socialShareTitle: PropTypes.string,

  /** Social media preview snippet (og:description). */
  socialShareDescription: PropTypes.string,

  /** Canonical page URL for <link rel="canonical" /> and og:url.
   * Override ONLY if a page's URL supports multiple URLs for the same page/same content.
   * Defaults to window.location.href */
  url: PropTypes.string,

  /** The site name sharing the content */
  siteName: PropTypes.string,

  /** The type of content being shared. */
  type: PropTypes.oneOf(['article', 'website', 'profile', 'video']),

  /** The twitter @name to reference for the content */
  twitterSite: PropTypes.string,

  /** The twitter @name to reference as the creator of the content */
  twitterCreator: PropTypes.string,

  /** Type of Twitter Card (see https://developer.twitter.com/en/docs/twitter-for-websites/cards/overview/abouts-cards) */
  twitterCard: PropTypes.string,

  /** Includes hreflang link tags on your page. To be used when your page has the language in the url. Set the `url` prop if you need the href to be different then the current url. */
  includeHreflang: PropTypes.bool,

  /**
   * hreflangConfig:
   * - languages: Array of language codes for pages not in all default languages (e.g., ['bg', 'ceb', 'zh-hans']).
   * - languageUrlMap: Object mapping language codes to language-specific URLs (e.g., {"de": "/stammbaum", "es": "/arbol-genealogico",}).
   * - Both languages and languageUrlMap are optional. If not provided, the component defaults to the app's languages and main Url.
   */

  hreflangConfig: PropTypes.shape({
    languages: PropTypes.array,
    languageUrlMap: PropTypes.object,
  }),

  /**
   * linkConfig:
   * - includeParams: Array of query parameter names to include in the canonical URL. If undefined, all parameters are included. If empty, no parameters are included. If array with values, only the specified parameters are included.
   * - excludeParams: Array of query parameter names to exclude from the canonical URL.
   */

  linkConfig: PropTypes.shape({
    includeParams: PropTypes.array,
    excludeParams: PropTypes.array,
  }),
}
